import { useMemo } from 'react';
import { AppUser, UserRole } from '../types';
import { BulkSelectionPayload } from '../server/bulk/bulkTypes';
import { UseBulkSelectionReturn } from './useBulkSelection';
import { buildBackendFilterPayload, BrokerFilterHookInput } from './propertyFilterPayloadBuilder';

export interface UseClientExportPayloadOptions {
  appUser: AppUser | null;
  bulkSelection: UseBulkSelectionReturn;
  filterInput: BrokerFilterHookInput;
  totalMatchingPropertiesCount: number;
}

/**
 * Hook para construir el payload exacto de exportación o selección masiva
 * para el cliente o broker. Asegura que cuando todos los resultados están seleccionados
 * o cuando se está en modo ALL, se envíe el payload completo de búsqueda para exportar
 * la totalidad del catálogo filtrado y no solo la página actual de 20 elementos.
 */
export function useClientExportPayload({
  appUser,
  bulkSelection,
  filterInput,
  totalMatchingPropertiesCount,
}: UseClientExportPayloadOptions): BulkSelectionPayload {
  return useMemo(() => {
    const isClient = appUser?.role === UserRole.CLIENTE;
    const activeRole = isClient ? UserRole.CLIENTE : (appUser?.role || UserRole.BROKER);

    const baseFilterPayload = buildBackendFilterPayload(filterInput, 1, 50000);
    baseFilterPayload.role = activeRole;

    const allowedOwners = isClient ? 'ALL' : (appUser?.allowedOwners ?? 'ALL');

    // Si el usuario seleccionó "Todos los resultados" en la UI o el hook está en modo 'ALL'
    if (bulkSelection.selectionMode === 'ALL' || bulkSelection.isAllSelected) {
      return {
        mode: 'ALL',
        excludedIds: bulkSelection.excludedIds,
        filters: baseFilterPayload,
        userRole: activeRole,
        allowedOwners,
      };
    }

    // Si hay IDs explícitos seleccionados manualmente
    if (bulkSelection.selectedIds.length > 0) {
      return {
        mode: 'EXPLICIT',
        selectedIds: bulkSelection.selectedIds,
        filters: baseFilterPayload,
        userRole: activeRole,
        allowedOwners,
      };
    }

    // Fallback: Si no hay selección manual explícita pero se dispara una acción masiva,
    // utilizar el filtro activo completo
    return {
      mode: 'ALL',
      excludedIds: [],
      filters: baseFilterPayload,
      userRole: activeRole,
      allowedOwners,
    };
  }, [
    appUser,
    bulkSelection.selectionMode,
    bulkSelection.isAllSelected,
    bulkSelection.excludedIds,
    bulkSelection.selectedIds,
    filterInput,
    totalMatchingPropertiesCount,
  ]);
}
